import { FC, lazy, Suspense, useCallback, useRef, useState } from 'react';
import { Button, Divider, Flex, Space, Typography } from 'antd';
import {
    TbArrowLeft,
    TbArrowRight,
    TbCopy,
    TbEdit,
    TbExternalLink,
    TbFile,
    TbHighlight,
    TbLayoutSidebarRightCollapse,
    TbLayoutSidebarRightExpand,
    TbPaperclip,
    TbRectangle,
    TbRestore,
    TbRotate,
    TbShieldLock,
    TbTopologyStar3,
    TbX,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AttachmentDestination, CloudAttachment, CloudStorageConnection } from '@/services';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import CloudStorageConnectionAvatar from '@/shared/components/dropdowns/SelectDataSourceDropdown/CloudStorageConnectionAvatar';
import ResourceHeader from '@/shared/components/layouts/headers/ResourceHeader/ResourceHeaderBase';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import LoadingContent from '@/shared/components/layouts/views/LoadingContent';
import { toast } from '@/shared/components/toasts/PlatformToast';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import WorkspaceResourceAvatar, {
    WorkspaceResourceGlyph,
} from '@/shared/components/workspace-resources/WorkspaceResourceAvatar';
import { formatDate } from '@/utilities/helpers/formatDateTime';

import AttachmentDocumentReviewHeader from '../AttachmentDocumentReviewHeader';
import AttachmentDocumentSider from '../AttachmentDocumentSider';
import AttachmentFileAvatar from '../AttachmentFileAvatar';
import AttachmentItemActions from '../AttachmentFileList/components/AttachmentItemActions';
import AttachmentPreview, { AttachmentDocumentReview, DOCX_MIME_TYPE } from '../AttachmentPreview';
import type { AttachmentRedactionEditorHandle, AttachmentRedactionState } from '../AttachmentRedactionEditor';
import DocumentConceptSider from '../AttachmentRedactionEditor/DocumentConceptSider';
import { EMPTY_DOCUMENT_INTELLIGENCE } from '../AttachmentRedactionEditor/documentIntelligence';
import { RedactionTool } from '../AttachmentRedactionEditor/redactionTypes';
import type { ExtractedTextSelection } from '../documentExtraction';
import DocuSignActionDropdown from '../DocuSignActionDropdown';
import OntologyDocumentReader from '../OntologyDocumentReader';

const AttachmentRedactionEditor = lazy(() => import('../AttachmentRedactionEditor'));

export type AttachmentDocumentMode = 'ontology' | 'preview' | 'redact';

interface AttachmentDocumentViewProps {
    attachment?: CloudAttachment;
    canComment?: boolean;
    connection?: CloudStorageConnection;
    destination?: AttachmentDestination;
    error?: string;
    loading?: boolean;
    onAttachmentCreated?: (attachment: CloudAttachment) => void;
    onAttachmentsBreadcrumbNavigate?: () => void;
    onClose: () => void;
    onModeChange?: (mode: AttachmentDocumentMode) => void;
    onResourceBreadcrumbNavigate?: () => void;
    resourceBreadcrumbLabel?: string;
    resourceBreadcrumbPath?: string;
}

const INITIAL_REDACTION_STATE: AttachmentRedactionState = {
    canRedo: false,
    canUndoCurrentPage: false,
    conceptSiderCollapsed: false,
    currentPage: 1,
    loading: false,
    pageCount: 1,
    redactionCount: 0,
    saving: false,
};

const ignoreReferenceSelection = () => undefined;

const AttachmentDocumentView: FC<AttachmentDocumentViewProps> = ({
    attachment,
    canComment,
    connection,
    destination,
    error,
    loading = false,
    onAttachmentCreated,
    onAttachmentsBreadcrumbNavigate,
    onClose,
    onModeChange,
    onResourceBreadcrumbNavigate,
    resourceBreadcrumbLabel,
    resourceBreadcrumbPath,
}) => {
    const { token } = useAntDesignTheme();
    const redactionEditorRef = useRef<AttachmentRedactionEditorHandle>(null);
    const [mode, setMode] = useState<AttachmentDocumentMode>('preview');
    const [previewReview, setPreviewReview] = useState<AttachmentDocumentReview>();
    const [ontologyReview, setOntologyReview] = useState<AttachmentDocumentReview>();
    const [contextReview, setContextReview] = useState<AttachmentDocumentReview>();
    const [previewSiderCollapsed, setPreviewSiderCollapsed] = useState(false);
    const [ontologySiderCollapsed, setOntologySiderCollapsed] = useState(false);
    const [pendingRedaction, setPendingRedaction] = useState<ExtractedTextSelection>();
    const [sourcePage, setSourcePage] = useState<number>();
    const [redactionTool, setRedactionTool] = useState<RedactionTool>('select');
    const [redactionState, setRedactionState] = useState<AttachmentRedactionState>(INITIAL_REDACTION_STATE);
    const supportsRedaction = Boolean(
        destination &&
        attachment &&
        ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(attachment.mimeType),
    );
    const supportsOntology = Boolean(
        attachment &&
        [DOCX_MIME_TYPE, 'application/json', 'application/pdf', 'text/csv', 'text/plain'].includes(attachment.mimeType),
    );
    const pluralResourceLabel =
        resourceBreadcrumbLabel ??
        (destination ? `${destination.resourceType[0].toUpperCase()}${destination.resourceType.slice(1)}s` : 'Search');

    const changeMode = useCallback(
        (nextMode: AttachmentDocumentMode) => {
            if (nextMode === 'redact') {
                setRedactionTool(attachment?.mimeType === 'application/pdf' ? 'select' : 'draw');
            }
            if (nextMode !== 'redact') {
                setPendingRedaction(undefined);
            }
            setMode(nextMode);
            onModeChange?.(nextMode);
        },
        [attachment?.mimeType, onModeChange],
    );

    const openOriginal = () => {
        if (attachment?.webViewLink) {
            window.open(attachment.webViewLink, '_blank', 'noopener,noreferrer');
        }
    };

    const copyLink = async () => {
        if (!attachment?.webViewLink) {
            return;
        }

        await navigator.clipboard.writeText(attachment.webViewLink);
        toast.success('Attachment link copied');
    };

    const handleRedactionCreated = useCallback(
        (created: CloudAttachment) => {
            onAttachmentCreated?.(created);
            changeMode('preview');
        },
        [changeMode, onAttachmentCreated],
    );

    const viewOntologySelectionInSource = useCallback(
        (selection: ExtractedTextSelection) => {
            const encodedPage = Number(selection.start.blockId.split(':')[1]);

            setSourcePage(selection.sourceRectangles[0]?.page ?? (Number.isFinite(encodedPage) ? encodedPage : 1));
            changeMode('preview');
        },
        [changeMode],
    );

    const securelyRedactOntologySelection = useCallback(
        (selection: ExtractedTextSelection) => {
            if (!selection.sourceRectangles.length || attachment?.mimeType !== 'application/pdf') {
                toast.error('Secure semantic redaction currently requires a source-aware PDF selection');
                return;
            }

            setPendingRedaction(selection);
            changeMode('redact');
        },
        [attachment?.mimeType, changeMode],
    );

    const headerActions = (
        <>
            {attachment ? (
                <AttachmentItemActions
                    attachment={attachment}
                    onOntologyMode={supportsOntology && mode !== 'ontology' ? () => changeMode('ontology') : undefined}
                    onRedactionMode={supportsRedaction && mode !== 'redact' ? () => changeMode('redact') : undefined}
                />
            ) : null}
            {attachment ? <Divider type='vertical' /> : null}
            <SmallTooltip placement='bottomRight' title='Close Attachment'>
                <Button
                    aria-label='Close attachment'
                    disabled={redactionState.saving}
                    icon={<TbX style={{ color: token.colorTextTertiary }} />}
                    size='small'
                    onClick={onClose}
                />
            </SmallTooltip>
        </>
    );
    const breadcrumbItems = [
        ...(destination
            ? [
                  {
                      key: 'resource-type',
                      icon: <WorkspaceResourceGlyph resource={destination.resourceType} size='1rem' />,
                      label: pluralResourceLabel,
                      to: resourceBreadcrumbPath,
                  },
                  {
                      key: 'resource',
                      icon: <WorkspaceResourceAvatar resource={destination.resourceType} size={20} />,
                      label: destination.resourceName,
                      onClick: onResourceBreadcrumbNavigate,
                  },
                  {
                      key: 'attachments',
                      icon: <TbPaperclip size='1rem' />,
                      label: 'Attachments',
                      onClick: onAttachmentsBreadcrumbNavigate,
                  },
              ]
            : [{ key: 'search', label: pluralResourceLabel, onClick: onResourceBreadcrumbNavigate }]),
        {
            key: 'connection',
            icon: <CloudStorageConnectionAvatar connection={connection ?? {}} size={20} />,
            label: connection?.type ?? 'Storage',
            onClick: onAttachmentsBreadcrumbNavigate,
        },
        {
            key: 'attachment',
            icon: attachment ? <AttachmentFileAvatar mimeType={attachment.mimeType} size={20} /> : undefined,
            label: attachment?.name ?? 'Attachment',
        },
    ];

    const previewActions =
        attachment && connection ? (
            <Flex align='center' justify='space-between' style={{ width: '100%' }} wrap='wrap' gap='0.5rem'>
                <Space.Compact size='small'>
                    <Button disabled={!attachment.webViewLink} icon={<TbExternalLink />} onClick={openOriginal}>
                        Open Original
                    </Button>
                    <Button disabled={!attachment.webViewLink} icon={<TbCopy />} onClick={() => void copyLink()}>
                        Copy Link
                    </Button>
                </Space.Compact>
                <Flex align='center' gap='0.5rem'>
                    <Button
                        disabled={!supportsOntology}
                        icon={<TbTopologyStar3 />}
                        size='small'
                        title={
                            supportsOntology
                                ? undefined
                                : 'Ontology Mode supports PDF, DOCX, JSON, CSV, and plain-text attachments'
                        }
                        onClick={() => changeMode('ontology')}
                    >
                        Ontology Mode
                    </Button>
                    {destination ? (
                        <>
                            <Divider type='vertical' />
                            <DocuSignActionDropdown
                                attachment={attachment}
                                connection={connection}
                                destination={destination}
                            />
                        </>
                    ) : null}
                    <Divider type='vertical' />
                    <SmallTooltip
                        placement='bottomRight'
                        title={`${previewSiderCollapsed ? 'Expand' : 'Collapse'} File Review`}
                    >
                        <Button
                            aria-label={`${previewSiderCollapsed ? 'Expand' : 'Collapse'} file review`}
                            icon={
                                previewSiderCollapsed ? (
                                    <TbLayoutSidebarRightExpand style={{ color: token.colorTextTertiary }} />
                                ) : (
                                    <TbLayoutSidebarRightCollapse style={{ color: token.colorTextTertiary }} />
                                )
                            }
                            size='small'
                            onClick={() => setPreviewSiderCollapsed((collapsed) => !collapsed)}
                        />
                    </SmallTooltip>
                </Flex>
            </Flex>
        ) : null;

    const ontologyActions = (
        <Flex align='center' justify='space-between' style={{ width: '100%' }} wrap='wrap' gap='0.5rem'>
            <Button icon={<TbArrowLeft />} size='small' onClick={() => changeMode('preview')}>
                Back to Preview
            </Button>
            <Flex align='center' gap='0.5rem'>
                <Typography.Text type='secondary'>Semantic, normalized document</Typography.Text>
                <Divider type='vertical' />
                <SmallTooltip
                    placement='bottomRight'
                    title={`${ontologySiderCollapsed ? 'Expand' : 'Collapse'} File Review`}
                >
                    <Button
                        aria-label={`${ontologySiderCollapsed ? 'Expand' : 'Collapse'} file review`}
                        icon={
                            ontologySiderCollapsed ? (
                                <TbLayoutSidebarRightExpand style={{ color: token.colorTextTertiary }} />
                            ) : (
                                <TbLayoutSidebarRightCollapse style={{ color: token.colorTextTertiary }} />
                            )
                        }
                        size='small'
                        onClick={() => setOntologySiderCollapsed((collapsed) => !collapsed)}
                    />
                </SmallTooltip>
            </Flex>
        </Flex>
    );

    const redactionActions = (
        <Flex align='center' justify='space-between' style={{ width: '100%' }} wrap='wrap' gap='0.5rem'>
            <Flex align='center' gap='0.5rem'>
                <div style={{ flex: 'none', width: '10rem' }}>
                    <DropdownButtonSelect<RedactionTool>
                        disabled={redactionState.saving}
                        matchTriggerWidth={false}
                        menuWidth='15rem'
                        options={[
                            {
                                description: 'Select text, then comment, copy, or redact it.',
                                disabled: attachment?.mimeType !== 'application/pdf',
                                icon: <TbHighlight />,
                                label: 'Highlight Text',
                                value: 'select',
                            },
                            {
                                description: 'Draw a secure redaction over any page content.',
                                icon: <TbRectangle />,
                                label: 'Draw Redaction',
                                value: 'draw',
                            },
                        ]}
                        size='small'
                        value={redactionTool}
                        onChange={(value) => value && setRedactionTool(value)}
                    />
                </div>
                <Divider type='vertical' />
                <Space.Compact size='small'>
                    <Button
                        disabled={!redactionState.canUndoCurrentPage || redactionState.saving}
                        icon={<TbRestore />}
                        onClick={() => redactionEditorRef.current?.undoCurrentPage()}
                    >
                        Undo
                    </Button>
                    <Button
                        disabled={!redactionState.canRedo || redactionState.saving}
                        icon={<TbRotate />}
                        onClick={() => redactionEditorRef.current?.redo()}
                    >
                        Redo
                    </Button>
                    <Button
                        disabled={!redactionState.canUndoCurrentPage || redactionState.saving}
                        icon={<TbEdit />}
                        onClick={() => redactionEditorRef.current?.clearCurrentPage()}
                    >
                        Clear Page
                    </Button>
                </Space.Compact>
            </Flex>
            <Flex align='center' gap='0.5rem'>
                <Space.Compact size='small'>
                    <Button
                        aria-label='Previous page'
                        disabled={redactionState.currentPage === 1 || redactionState.saving}
                        icon={<TbArrowLeft />}
                        onClick={() => redactionEditorRef.current?.previousPage()}
                    />
                    <Button style={{ pointerEvents: 'none' }}>
                        Page {redactionState.currentPage} of {redactionState.pageCount}
                    </Button>
                    <Button
                        aria-label='Next page'
                        disabled={redactionState.currentPage === redactionState.pageCount || redactionState.saving}
                        icon={<TbArrowRight />}
                        onClick={() => redactionEditorRef.current?.nextPage()}
                    />
                </Space.Compact>
                <Divider type='vertical' />
                <SmallTooltip
                    placement='bottomRight'
                    title={`${redactionState.conceptSiderCollapsed ? 'Expand' : 'Collapse'} File Review`}
                >
                    <Button
                        aria-label={`${redactionState.conceptSiderCollapsed ? 'Expand' : 'Collapse'} file review`}
                        icon={
                            redactionState.conceptSiderCollapsed ? (
                                <TbLayoutSidebarRightExpand style={{ color: token.colorTextTertiary }} />
                            ) : (
                                <TbLayoutSidebarRightCollapse style={{ color: token.colorTextTertiary }} />
                            )
                        }
                        size='small'
                        onClick={() => redactionEditorRef.current?.toggleConceptSider()}
                    />
                </SmallTooltip>
            </Flex>
        </Flex>
    );

    return (
        <section style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <ResourceHeader
                breadcrumbs={breadcrumbItems}
                rightActions={headerActions}
                style={{ flex: 'none', padding: '0.75rem 1rem' }}
            />
            <div
                style={{
                    borderBottom: `1px solid ${token.colorBorder}`,
                    flex: 'none',
                    padding: '0.75rem 1rem',
                }}
            >
                {mode === 'redact' ? redactionActions : mode === 'ontology' ? ontologyActions : previewActions}
            </div>
            <div style={{ flex: '1 1 0%', minHeight: 0, overflow: 'hidden', padding: '1rem' }}>
                {loading ? (
                    <LoadingContent />
                ) : attachment && connection && !error ? (
                    <Flex
                        vertical
                        style={{
                            border: `1px solid ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            height: '100%',
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <AttachmentDocumentReviewHeader
                            destination={destination}
                            intelligence={
                                mode === 'redact'
                                    ? contextReview?.intelligence
                                    : mode === 'ontology'
                                      ? ontologyReview?.intelligence
                                      : previewReview?.intelligence
                            }
                            loading={
                                mode === 'redact'
                                    ? contextReview?.loading
                                    : mode === 'ontology'
                                      ? ontologyReview?.loading
                                      : previewReview?.loading
                            }
                        />
                        <div style={{ flex: '1 1 0%', minHeight: 0, overflow: 'hidden' }}>
                            {mode === 'redact' && destination ? (
                                <Suspense fallback={<LoadingContent />}>
                                    <AttachmentRedactionEditor
                                        ref={redactionEditorRef}
                                        attachment={attachment}
                                        canComment={canComment}
                                        connection={connection}
                                        destination={destination}
                                        initialRectangles={pendingRedaction?.sourceRectangles}
                                        tool={redactionTool}
                                        onCreated={handleRedactionCreated}
                                        onReviewChange={setContextReview}
                                        onStateChange={setRedactionState}
                                        onUnavailable={() => changeMode('preview')}
                                    />
                                </Suspense>
                            ) : mode === 'ontology' ? (
                                <Flex style={{ height: '100%', minHeight: 0 }}>
                                    <div style={{ flex: '1 1 0%', minHeight: 0, minWidth: 0 }}>
                                        <OntologyDocumentReader
                                            key={`ontology:${connection.id}:${attachment.id}:${destination?.resourceId ?? 'connected-file'}`}
                                            attachment={attachment}
                                            canComment={canComment}
                                            connection={connection}
                                            destination={destination}
                                            onReviewChange={setOntologyReview}
                                            onSecureRedact={
                                                destination && attachment.mimeType === 'application/pdf'
                                                    ? securelyRedactOntologySelection
                                                    : undefined
                                            }
                                            onViewSource={viewOntologySelectionInSource}
                                        />
                                    </div>
                                    <AttachmentDocumentSider
                                        activeConceptReferenceId={ontologyReview?.activeReferenceId}
                                        attachment={attachment}
                                        canComment={canComment}
                                        collapsed={ontologySiderCollapsed}
                                        concepts={
                                            <DocumentConceptSider
                                                key={
                                                    ontologyReview?.activeReferenceId ?? 'unselected-ontology-concepts'
                                                }
                                                activeReferenceId={ontologyReview?.activeReferenceId}
                                                intelligence={
                                                    ontologyReview?.intelligence ?? EMPTY_DOCUMENT_INTELLIGENCE
                                                }
                                                loading={ontologyReview?.loading ?? true}
                                                textExtractionSupported={
                                                    ontologyReview?.textExtractionSupported ?? true
                                                }
                                                onReferenceSelect={
                                                    ontologyReview?.onReferenceSelect ?? ignoreReferenceSelection
                                                }
                                            />
                                        }
                                        destination={destination}
                                    />
                                </Flex>
                            ) : (
                                <Flex style={{ height: '100%', minHeight: 0 }}>
                                    <div style={{ flex: '1 1 0%', minHeight: 0, minWidth: 0 }}>
                                        <AttachmentPreview
                                            key={`${connection.id}:${attachment.id}:${destination?.resourceId ?? 'connected-file'}`}
                                            attachment={attachment}
                                            bordered={false}
                                            connection={connection}
                                            destination={destination}
                                            sourcePage={sourcePage}
                                            onReviewChange={setPreviewReview}
                                        />
                                    </div>
                                    <AttachmentDocumentSider
                                        attachment={attachment}
                                        canComment={canComment}
                                        collapsed={previewSiderCollapsed}
                                        concepts={
                                            previewReview || attachment.mimeType === DOCX_MIME_TYPE ? (
                                                <DocumentConceptSider
                                                    intelligence={
                                                        previewReview?.intelligence ?? EMPTY_DOCUMENT_INTELLIGENCE
                                                    }
                                                    loading={previewReview?.loading ?? true}
                                                    textExtractionSupported={
                                                        previewReview?.textExtractionSupported ?? true
                                                    }
                                                    onReferenceSelect={
                                                        previewReview?.onReferenceSelect ?? ignoreReferenceSelection
                                                    }
                                                />
                                            ) : undefined
                                        }
                                        destination={destination}
                                    />
                                </Flex>
                            )}
                        </div>
                    </Flex>
                ) : (
                    <EmptyView
                        description={error ?? 'Select a file to load an authenticated preview.'}
                        height='100%'
                        icon={<TbPaperclip style={{ color: token.colorTextTertiary }} />}
                        label='Preview Unavailable'
                    />
                )}
            </div>
            {attachment && (mode === 'preview' || mode === 'ontology') ? (
                <Flex
                    align='center'
                    justify='space-between'
                    gap='0.375rem'
                    style={{
                        borderTop: `1px solid ${token.colorBorder}`,
                        boxSizing: 'border-box',
                        color: token.colorTextTertiary,
                        flex: 'none',
                        height: '3rem',
                        padding: '0.75rem 1rem',
                    }}
                >
                    <Flex align='center' gap='0.375rem'>
                        {mode === 'ontology' ? <TbTopologyStar3 /> : <TbFile />}
                        <small>{mode === 'ontology' ? 'Normalized semantic text' : attachment.mimeType}</small>
                        {attachment.createdTime ? (
                            <small> · Added {formatDate(new Date(attachment.createdTime))}</small>
                        ) : null}
                    </Flex>
                    {mode === 'ontology' ? <small>Hiding text here is not secure redaction.</small> : null}
                </Flex>
            ) : mode === 'redact' ? (
                <Flex
                    align='center'
                    justify='space-between'
                    style={{
                        borderTop: `1px solid ${token.colorBorder}`,
                        boxSizing: 'border-box',
                        flex: 'none',
                        height: '3rem',
                        padding: '0.75rem 1rem',
                    }}
                >
                    <Typography.Text type='secondary'>
                        {redactionState.redactionCount} redaction{redactionState.redactionCount === 1 ? '' : 's'} marked
                    </Typography.Text>
                    <Space size={8}>
                        <Button disabled={redactionState.saving} size='small' onClick={() => changeMode('preview')}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!redactionState.redactionCount || redactionState.loading}
                            icon={<TbShieldLock />}
                            loading={redactionState.saving}
                            size='small'
                            type='primary'
                            onClick={() => void redactionEditorRef.current?.saveRedactedCopy()}
                        >
                            Create Redacted Copy
                        </Button>
                    </Space>
                </Flex>
            ) : null}
        </section>
    );
};

export default AttachmentDocumentView;
